import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { BloodworkBadge } from "../components/Badges";
import { PricingBlock } from "../components/Pricing";
import Disclaimer from "../components/Disclaimer";
import { CallCard } from "../components/PhoneCTA";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { CheckCircle2, AlertTriangle, FlaskConical, ArrowLeft, Sparkles } from "lucide-react";
import ProviderEvaluationRequired from "../components/ProviderEvaluationRequired";
import InterestedInThisTreatment from "../components/InterestedInThisTreatment";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} className="py-8 border-t border-stone-200">
      <h2 className="font-display text-2xl mb-4">{title}</h2>
      {children}
    </section>
  );
}

export default function TherapyDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setT(null); setRelated([]); setErr(null);
    api.get(`/therapies/${slug}`).then(({ data }) => {
      setT(data);
      // Fetch related: therapies sharing at least one category, excluding self
      if (data.categories?.[0]) {
        api.get("/therapies", { params: { category: data.categories[0], public_only: true } })
          .then(({ data: rel }) => setRelated(rel.filter((r) => r.slug !== data.slug).slice(0, 3)))
          .catch(() => {});
      }
    }).catch(() => setErr("Therapy not found."));
  }, [slug]);

  if (err) return <div className="vv-container py-24 text-stone-500">{err}</div>;
  if (!t) return <div className="vv-container py-24 text-stone-500">Loading…</div>;

  const takeaways = [
    `${t.public_name} is ${t.regulatory_status === "fda_approved" ? "an FDA-approved medication" : t.regulatory_status === "off_label" ? "sometimes discussed off-label" : t.regulatory_status === "compounded" ? "available as a compounded prescription (not FDA-approved)" : "an educational topic currently being studied"}.`,
    t.bloodwork_level === "baseline_required" ? "Baseline labs are generally required before a provider considers this therapy." : t.bloodwork_level === "recent_recommended" ? "Recent labs are recommended before evaluation." : "Your provider determines whether testing is needed.",
    `Providers may evaluate it in ${(t.categories || []).slice(0,3).join(", ") || "select clinical categories"}.`,
    "A licensed VidaVital provider must complete a live consultation before any treatment is prescribed.",
  ];

  const quickAnswer = `${t.public_name} is ${t.short_description.replace(/\.$/, "")}. Whether it is appropriate depends on your medical history, goals, current medications, and testing — evaluated by a licensed VidaVital Medical provider during a live consultation. Educational content only; not medical advice.`;

  // Retatrutide: FDA Phase-3 investigational compound that cannot be compounded.
  // No booking CTAs, no access language anywhere on this detail page.
  const isInvestigationalNoCTA = t.slug === "retatrutide-glp-3";

  const medTherapySchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    name: t.public_name,
    alternateName: t.clinical_name || undefined,
    description: t.short_description,
    url: `${window.location.origin}/peptide-library/${t.slug}`,
    medicineSystem: "https://schema.org/WesternConventional",
    relevantSpecialty: "https://schema.org/PrimaryCare",
  };
  const medWebPageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: `${t.public_name} — Uses, Evidence, Bloodwork & Consultation`,
    url: `https://medicalpeptides.ai/peptide-library/${t.slug}`,
    inLanguage: "en-US",
    lastReviewed: (t.updated_at || "").slice(0, 10) || undefined,
    reviewedBy: { "@type": "Organization", name: "VidaVital Medical" },
    audience: { "@type": "MedicalAudience", audienceType: "Patient" },
    about: { "@type": "MedicalTherapy", name: t.public_name },
  };
  const faqSchema = (t.faq_items?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.faq_items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null);
  const schemas = [medTherapySchema, medWebPageSchema];
  if (faqSchema) schemas.push(faqSchema);

  return (
    <div data-testid="therapy-detail-page">
      <SEO
        title={`${t.public_name} — Uses, Evidence, Bloodwork & Consultation`}
        description={`Educational overview of ${t.public_name}: potential support areas, evidence status, bloodwork considerations, and how a licensed VidaVital provider evaluates it.`}
        path={`/peptide-library/${t.slug}`}
        schema={schemas}
        breadcrumbs={[
          { name: "Peptide Library", path: "/peptide-library" },
          { name: t.public_name, path: `/peptide-library/${t.slug}` },
        ]}
      />
      <section className="vv-hero">
        <div className="vv-container py-14">
          <Link to="/peptide-library" data-testid="detail-back" className="text-sm text-stone-500 hover:text-[color:var(--vv-teal)] inline-flex items-center gap-1"><ArrowLeft size={14} /> Peptide Library</Link>
          <h1 className="font-display text-4xl md:text-5xl mt-4">{t.public_name}</h1>
          {t.clinical_name && <div className="text-stone-500 italic mt-1">{t.clinical_name}</div>}
          <p className="mt-4 max-w-3xl text-stone-600 leading-relaxed">{t.short_description}</p>
          <div className="flex flex-wrap gap-2 mt-6">
            <BloodworkBadge level={t.bloodwork_level} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {!isInvestigationalNoCTA && (
              <ScheduleConsultationCTA
                location={`therapy-hero-${t.slug}`}
                variant="gold"
                size="lg"
              >
                Start Consultation
              </ScheduleConsultationCTA>
            )}
            {isInvestigationalNoCTA && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-5 py-3 text-amber-900 text-sm max-w-xl leading-relaxed"
                   data-testid="therapy-investigational-banner">
                <strong>Educational page only.</strong> Retatrutide is investigational and not FDA-approved. Under current FDA policy it cannot be used in compounding for individual patients.{" "}
                <a href="/peptide-library" className="underline hover:text-amber-800">View available therapies →</a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Phase A/D — Provider Evaluation Required block above the fold.
          Suppressed for retatrutide (FDA compounding ban). */}
      {!isInvestigationalNoCTA && (
        <section className="vv-container pt-8">
          <ProviderEvaluationRequired location={`therapy-eval-${t.slug}`} />
        </section>
      )}

      <section className="vv-container pt-8">
        <PricingBlock therapy={t} testId={`pricing-block-${t.slug}`} />
      </section>

      {/* Quick Answer (AISO citation block) */}
      <section className="vv-container pt-8">
        <div data-testid="quick-answer" className="rounded-2xl border border-stone-200 bg-white p-6 md:p-7">
          <div className="text-xs uppercase tracking-widest text-[color:var(--vv-gold-deep)] font-semibold flex items-center gap-2">
            <Sparkles size={14} /> Quick Answer
          </div>
          <p className="mt-2 text-stone-800 leading-relaxed">{quickAnswer}</p>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="vv-container pt-6">
        <div data-testid="key-takeaways" className="rounded-2xl bg-[color:var(--vv-alt)] border border-stone-200 p-6 md:p-7">
          <div className="text-xs uppercase tracking-widest text-[color:var(--vv-gold-deep)] font-semibold">Key Takeaways</div>
          <ul className="mt-3 space-y-2">
            {takeaways.map((k) => (
              <li key={k.slice(0, 60)} className="flex items-start gap-2 text-stone-800 text-sm leading-relaxed">
                <CheckCircle2 size={16} className="text-[color:var(--vv-gold-deep)] mt-0.5 flex-shrink-0" /> {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="vv-container">
        <Section testId="section-what" title="What is it?">
          <p className="text-stone-700 leading-relaxed max-w-3xl">{t.full_description}</p>
        </Section>

        <Section testId="section-support" title="What may it support?">
          <ul className="space-y-2 max-w-3xl">
            {t.potential_support_areas?.map((s) => (
              <li key={s} className="flex items-start gap-2 text-stone-700"><CheckCircle2 size={18} className="text-[color:var(--vv-teal)] mt-0.5" />{s}</li>
            ))}
          </ul>
        </Section>

        <Section testId="section-fit" title="Where might it fit?">
          <div className="flex flex-wrap gap-2">
            {t.tiers?.map((tier) => (
              <span key={tier} className="vv-badge border border-amber-200 bg-amber-50 text-amber-800 capitalize">{tier} pathway</span>
            ))}
            {t.categories?.map((c) => (
              <span key={c} className="vv-badge border border-stone-200 bg-stone-100 text-stone-700 capitalize">{c.replace(/-/g, " ")}</span>
            ))}
          </div>
        </Section>

        <Section testId="section-bloodwork" title="Bloodwork & monitoring">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500">Baseline status</div>
              <div className="mt-2"><BloodworkBadge level={t.bloodwork_level} /></div>
              {t.baseline_labs?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-widest text-stone-500">Common tests</div>
                  <ul className="mt-2 text-stone-700 list-disc pl-5">{t.baseline_labs.map((l) => <li key={l}>{l}</li>)}</ul>
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <FlaskConical className="text-[color:var(--vv-teal)]" />
              <div className="mt-2 text-sm text-stone-600">{t.monitoring_summary}</div>
              <div className="mt-3 text-xs text-stone-500">The provider determines final requirements.</div>
            </div>
          </div>
        </Section>

        <Section testId="section-side-effects" title="Potential side effects">
          <ul className="grid md:grid-cols-2 gap-2 max-w-3xl">
            {t.common_side_effects?.map((s) => <li key={s} className="flex items-start gap-2 text-stone-700"><AlertTriangle size={16} className="text-amber-600 mt-0.5" /> {s}</li>)}
            {t.serious_risks?.map((s) => <li key={s} className="flex items-start gap-2 text-stone-700"><AlertTriangle size={16} className="text-red-600 mt-0.5" /> {s}</li>)}
          </ul>
        </Section>

        <Section testId="section-contraindications" title="Who may not be a candidate?">
          <ul className="space-y-1 text-stone-700">{t.contraindications?.map((c) => <li key={c}>• {c}</li>)}</ul>
        </Section>

        <Section testId="section-interactions" title="Drug interactions">
          <ul className="space-y-1 text-stone-700">{t.interactions?.map((c) => <li key={c}>• {c}</li>)}</ul>
        </Section>

        <Section testId="section-evidence" title="Regulatory disclosures">
          {t.approved_indication && <p className="mt-1 text-stone-700 max-w-3xl">{t.approved_indication}</p>}
          <div className="mt-6 space-y-3">
            {t.regulatory_status === "off_label" && <Disclaimer variant="off_label" testId="disclaimer-off-label" />}
            {t.regulatory_status === "compounded" && <Disclaimer variant="compounded" testId="disclaimer-compounded" />}
            {t.regulatory_status === "emerging" && <Disclaimer variant="emerging" testId="disclaimer-emerging" />}
          </div>
        </Section>

        {/* Consultation section — suppressed for retatrutide (investigational/no CTA). */}
        {!isInvestigationalNoCTA && (
          <Section testId="section-consultation" title="Speak With a Licensed Medical Provider">
            <p className="text-stone-700 leading-relaxed max-w-3xl">
              If you're interested in learning whether {t.public_name} may be appropriate for your health goals, schedule a consultation with a licensed VidaVital Medical provider. Your provider will review your medical history, discuss available treatment options, and determine whether any therapy is clinically appropriate for your individual needs.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <ScheduleConsultationCTA
                location={`therapy-consult-${t.slug}`}
                variant="gold"
                size="lg"
              />
              <a href="tel:+18887738677" className="vv-btn-outline text-sm">Call (888) 773-8677</a>
            </div>
            <div className="mt-6">
              <Disclaimer variant="consultation" testId="disclaimer-consultation" />
            </div>
          </Section>
        )}

        {t.faq_items?.length > 0 && (
          <Section testId="section-faq" title="Frequently asked questions">
            <div className="space-y-4 max-w-3xl">
              {t.faq_items.map((f) => (
                <div key={f.q} className="vv-card p-5">
                  <div className="font-semibold">{f.q}</div>
                  <div className="text-stone-600 text-sm mt-1">{f.a}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {t.references?.length > 0 && (
          <Section testId="section-sources" title="Sources">
            <ul className="text-sm text-stone-600 space-y-1">
              {t.references.map((r) => (
                <li key={r.title}>{r.title} {r.reviewed_on ? `— reviewed ${r.reviewed_on}` : ""} {r.reviewer ? `by ${r.reviewer}` : ""}</li>
              ))}
            </ul>
          </Section>
        )}

        {related.length > 0 && (
          <section data-testid="related-therapies" className="py-8 border-t border-stone-200">
            <h2 className="font-display text-2xl mb-4">Related therapies</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/peptide-library/${r.slug}`} data-testid={`related-${r.slug}`} className="vv-card p-5 block">
                  <div className="font-display text-lg">{r.public_name}</div>
                  <div className="text-xs text-stone-500 mt-1 line-clamp-2">{r.short_description}</div>
                  <div className="mt-3"><BloodworkBadge level={r.bloodwork_level} /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Phase A/D — Interested in this treatment? conversion block.
            Suppressed for investigational therapies (no FDA-cleared compounding path). */}
        {!isInvestigationalNoCTA && (
          <InterestedInThisTreatment
            location={`therapy-footer-${t.slug}`}
            topic={t.public_name}
          />
        )}

        <div className="py-10"><Disclaimer /></div>
      </div>
    </div>
  );
}
